/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Strip de alerta Square (proto `.strip` / `.strip.warn`).
 * Común a Catálogo / Stock / POS / Ventas: aviso accionable entre KPIs y lista.
 */
import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { SoftPressable } from '../ui/SoftPressable';
import { ticketColors, ticketFonts } from '../theme/public-api';

export type TicketAlertStripTone = 'default' | 'warn' | 'bad';

export type TicketAlertStripProps = {
  title: string;
  subtitle?: string | null;
  tone?: TicketAlertStripTone;
  onPress?: () => void;
  accessibilityLabel?: string;
  testID?: string;
  /** Si true, no muestra chevron (strip informativo). */
  hideChevron?: boolean;
  /** Una línea: título + subtítulo inline, padding reducido. */
  compact?: boolean;
};

export function TicketAlertStrip({
  title,
  subtitle,
  tone = 'warn',
  onPress,
  accessibilityLabel,
  testID,
  hideChevron = false,
  compact = false,
}: TicketAlertStripProps) {
  const stripStyle = [
    styles.strip,
    compact && styles.stripCompact,
    tone === 'warn' && styles.stripWarn,
    tone === 'bad' && styles.stripBad,
  ];
  const body = (
    <>
      <View style={[styles.dot, tone === 'warn' && styles.dotWarn, tone === 'bad' && styles.dotBad]} />
      <View style={styles.copy}>
        {compact ? (
          <Text style={styles.subtitle} numberOfLines={2}>
            <Text style={styles.titleInline}>{title}</Text>
            {subtitle ? ` · ${subtitle}` : ''}
          </Text>
        ) : (
          <>
            <Text style={styles.title}>{title}</Text>
            {subtitle ? <Text style={styles.subtitle}>{subtitle}</Text> : null}
          </>
        )}
      </View>
      {!hideChevron ? <Text style={styles.chevron}>›</Text> : null}
    </>
  );

  if (onPress) {
    return (
      <SoftPressable
        style={stripStyle}
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel ?? title}
        testID={testID}
        hoverLift={false}
      >
        {body}
      </SoftPressable>
    );
  }

  return (
    <View style={stripStyle} testID={testID} accessible accessibilityLabel={accessibilityLabel ?? title}>
      {body}
    </View>
  );
}

const styles = StyleSheet.create({
  strip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: ticketColors.paper,
    borderWidth: 1,
    borderColor: ticketColors.brandBorder,
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 14,
  },
  stripCompact: {
    paddingVertical: 8,
  },
  stripWarn: {
    backgroundColor: ticketColors.warnBg,
    borderColor: ticketColors.warnBorder,
  },
  stripBad: {
    backgroundColor: '#FEF2F2',
    borderColor: 'rgba(185,28,28,0.25)',
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: ticketColors.gold,
    flexShrink: 0,
  },
  dotWarn: {
    backgroundColor: ticketColors.warn,
  },
  dotBad: {
    backgroundColor: ticketColors.bad,
  },
  copy: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  title: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  titleInline: {
    fontWeight: '700',
    color: ticketColors.ink,
  },
  subtitle: {
    fontSize: 12,
    fontWeight: '500',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  chevron: {
    marginLeft: 'auto',
    color: ticketColors.ink3,
    fontSize: 18,
    fontWeight: '600',
    lineHeight: 20,
  },
});
